import type {GeoObjNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/obj/Geo';
import type {RootManagerNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/manager/Root';
import type {ExampleData, DataCallbackOptions} from '../../Common';
import type {ReturnedNodeDataDict} from '../../ExampleUtils';
import type {ActorSopNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/Actor';
import type {CameraControlsSopNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/CameraControls';
import type {MaterialsNetworkSopNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/MaterialsNetwork';
import {ParamType} from '../../../../../@polygonjs/polygonjs/src/engine/poly/ParamType';

export function createData(options: DataCallbackOptions): ExampleData {
	const {scene} = options;
	const root = scene.root();
	const scene_root_nodes: ReturnedNodeDataDict = {};
	function create_lights(parentNode: RootManagerNode) {
		var lights = parentNode.createNode('geo');
		lights.setName('lights');
		const lights_nodes: ReturnedNodeDataDict = {};
		function create_hemisphereLight1(lights: GeoObjNode) {
			var hemisphereLight1 = lights.createNode('hemisphereLight');
			hemisphereLight1.setName('hemisphereLight1');
			const hemisphereLight1_nodes: ReturnedNodeDataDict = {};
			hemisphereLight1.uiData.setPosition(50, -50);
			hemisphereLight1.p.intensity.set(0.52);
			hemisphereLight1.params.postCreateSpareParams();
			hemisphereLight1.params.runOnSceneLoadHooks();
			return {node: hemisphereLight1, children: hemisphereLight1_nodes};
		}
		function create_spotLight1(lights: GeoObjNode) {
			var spotLight1 = lights.createNode('spotLight');
			spotLight1.setName('spotLight1');
			const spotLight1_nodes: ReturnedNodeDataDict = {};
			spotLight1.uiData.setPosition(300, -50);
			spotLight1.p.decay.set(0.1);
			spotLight1.p.distance.set(10);
			spotLight1.p.castShadow.set(true);
			spotLight1.params.postCreateSpareParams();
			spotLight1.params.runOnSceneLoadHooks();
			return {node: spotLight1, children: spotLight1_nodes};
		}
		function create_polarTransform1(lights: GeoObjNode) {
			var polarTransform1 = lights.createNode('polarTransform');
			polarTransform1.setName('polarTransform1');
			const polarTransform1_nodes: ReturnedNodeDataDict = {};
			polarTransform1.uiData.setPosition(300, 150);
			polarTransform1.p.center.set([0, 0.7, 0]);
			polarTransform1.p.latitude.set(25.2);
			polarTransform1.p.depth.set(3);
			polarTransform1.params.postCreateSpareParams();
			polarTransform1.params.runOnSceneLoadHooks();
			return {node: polarTransform1, children: polarTransform1_nodes};
		}
		function create_merge1(lights: GeoObjNode) {
			var merge1 = lights.createNode('merge');
			merge1.setName('merge1');
			const merge1_nodes: ReturnedNodeDataDict = {};
			merge1.uiData.setPosition(100, 300);
			merge1.flags.display.set(true);
			merge1.params.postCreateSpareParams();
			merge1.params.runOnSceneLoadHooks();
			return {node: merge1, children: merge1_nodes};
		}
		lights_nodes['hemisphereLight1'] = create_hemisphereLight1(lights);
		lights_nodes['spotLight1'] = create_spotLight1(lights);
		lights_nodes['polarTransform1'] = create_polarTransform1(lights);
		lights_nodes['merge1'] = create_merge1(lights);
		lights_nodes['polarTransform1'].node.setInput(0, lights_nodes['spotLight1'].node);
		lights_nodes['merge1'].node.setInput(0, lights_nodes['hemisphereLight1'].node);
		lights_nodes['merge1'].node.setInput(1, lights_nodes['polarTransform1'].node);
		if (lights.childrenController) {
			lights.childrenController.selection.set([lights_nodes['hemisphereLight1'].node]);
		}
		lights.uiData.setPosition(-150, -250);
		lights.flags.display.set(true);
		lights.params.postCreateSpareParams();
		lights.params.runOnSceneLoadHooks();
		return {node: lights, children: lights_nodes};
	}
	function create_cameras(parentNode: RootManagerNode) {
		var cameras = parentNode.createNode('geo');
		cameras.setName('cameras');
		const cameras_nodes: ReturnedNodeDataDict = {};
		function create_cameraControls1(cameras: GeoObjNode) {
			var cameraControls1 = cameras.createNode('cameraControls');
			cameraControls1.setName('cameraControls1');
			const cameraControls1_nodes: ReturnedNodeDataDict = {};
			function create_cameraOrbitControls1(cameraControls1: CameraControlsSopNode) {
				var cameraOrbitControls1 = cameraControls1.createNode('cameraOrbitControls');
				cameraOrbitControls1.setName('cameraOrbitControls1');
				const cameraOrbitControls1_nodes: ReturnedNodeDataDict = {};
				cameraOrbitControls1.uiData.setPosition(0, 0);
				cameraOrbitControls1.p.target.set([0, 0.75, 0]);
				cameraOrbitControls1.params.postCreateSpareParams();
				cameraOrbitControls1.params.runOnSceneLoadHooks();
				return {node: cameraOrbitControls1, children: cameraOrbitControls1_nodes};
			}
			cameraControls1_nodes['cameraOrbitControls1'] = create_cameraOrbitControls1(cameraControls1);
			if (cameraControls1.childrenController) {
				cameraControls1.childrenController.selection.set([]);
			}
			cameraControls1.uiData.setPosition(0, 150);
			cameraControls1.p.node.set('cameraOrbitControls1');
			cameraControls1.params.postCreateSpareParams();
			cameraControls1.params.runOnSceneLoadHooks();
			return {node: cameraControls1, children: cameraControls1_nodes};
		}
		function create_perspectiveCamera_DEBUG(cameras: GeoObjNode) {
			var perspectiveCamera_DEBUG = cameras.createNode('perspectiveCamera');
			perspectiveCamera_DEBUG.setName('perspectiveCamera_DEBUG');
			const perspectiveCamera_DEBUG_nodes: ReturnedNodeDataDict = {};
			perspectiveCamera_DEBUG.uiData.setPosition(0, -50);
			perspectiveCamera_DEBUG.p.position.set([3.3, 3.3, 3.3]);
			perspectiveCamera_DEBUG.params.postCreateSpareParams();
			perspectiveCamera_DEBUG.params.runOnSceneLoadHooks();
			return {node: perspectiveCamera_DEBUG, children: perspectiveCamera_DEBUG_nodes};
		}
		function create_mapboxCamera1(cameras: GeoObjNode) {
			var mapboxCamera1 = cameras.createNode('mapboxCamera');
			mapboxCamera1.setName('mapboxCamera1');
			const mapboxCamera1_nodes: ReturnedNodeDataDict = {};
			mapboxCamera1.uiData.setPosition(200, 50);
			mapboxCamera1.params.postCreateSpareParams();
			mapboxCamera1.params.runOnSceneLoadHooks();
			return {node: mapboxCamera1, children: mapboxCamera1_nodes};
		}
		function create_merge1(cameras: GeoObjNode) {
			var merge1 = cameras.createNode('merge');
			merge1.setName('merge1');
			const merge1_nodes: ReturnedNodeDataDict = {};
			merge1.uiData.setPosition(50, 400);
			merge1.flags.display.set(true);
			merge1.params.postCreateSpareParams();
			merge1.params.runOnSceneLoadHooks();
			return {node: merge1, children: merge1_nodes};
		}
		cameras_nodes['cameraControls1'] = create_cameraControls1(cameras);
		cameras_nodes['perspectiveCamera_DEBUG'] = create_perspectiveCamera_DEBUG(cameras);
		cameras_nodes['mapboxCamera1'] = create_mapboxCamera1(cameras);
		cameras_nodes['merge1'] = create_merge1(cameras);
		cameras_nodes['cameraControls1'].node.setInput(0, cameras_nodes['perspectiveCamera_DEBUG'].node);
		cameras_nodes['merge1'].node.setInput(0, cameras_nodes['cameraControls1'].node);
		cameras_nodes['merge1'].node.setInput(1, cameras_nodes['mapboxCamera1'].node);
		if (cameras.childrenController) {
			cameras.childrenController.selection.set([cameras_nodes['mapboxCamera1'].node]);
		}
		cameras.uiData.setPosition(-150, -150);
		cameras.flags.display.set(true);
		cameras.params.postCreateSpareParams();
		cameras.params.runOnSceneLoadHooks();
		return {node: cameras, children: cameras_nodes};
	}
	function create_geo1(parentNode: RootManagerNode) {
		var geo1 = parentNode.createNode('geo');
		geo1.setName('geo1');
		const geo1_nodes: ReturnedNodeDataDict = {};
		function create_emptyObject1(geo1: GeoObjNode) {
			var emptyObject1 = geo1.createNode('emptyObject');
			emptyObject1.setName('emptyObject1');
			const emptyObject1_nodes: ReturnedNodeDataDict = {};
			emptyObject1.uiData.setPosition(-350, 850);
			emptyObject1.params.postCreateSpareParams();
			emptyObject1.params.runOnSceneLoadHooks();
			return {node: emptyObject1, children: emptyObject1_nodes};
		}
		function create_transform4(geo1: GeoObjNode) {
			var transform4 = geo1.createNode('transform');
			transform4.setName('transform4');
			const transform4_nodes: ReturnedNodeDataDict = {};
			transform4.uiData.setPosition(50, 1050);
			transform4.p.scale.set(16);
			transform4.params.postCreateSpareParams();
			transform4.params.runOnSceneLoadHooks();
			return {node: transform4, children: transform4_nodes};
		}
		function create_text1(geo1: GeoObjNode) {
			var text1 = geo1.createNode('text');
			text1.setName('text1');
			const text1_nodes: ReturnedNodeDataDict = {};
			text1.uiData.setPosition(50, 850);
			text1.p.text.set('95');
			text1.params.postCreateSpareParams();
			text1.params.runOnSceneLoadHooks();
			return {node: text1, children: text1_nodes};
		}
		function create_OUT_geometry(geo1: GeoObjNode) {
			var OUT_geometry = geo1.createNode('null');
			OUT_geometry.setName('OUT_geometry');
			const OUT_geometry_nodes: ReturnedNodeDataDict = {};
			OUT_geometry.uiData.setPosition(50, 1300);
			OUT_geometry.params.postCreateSpareParams();
			OUT_geometry.params.runOnSceneLoadHooks();
			return {node: OUT_geometry, children: OUT_geometry_nodes};
		}
		function create_material1(geo1: GeoObjNode) {
			var material1 = geo1.createNode('material');
			material1.setName('material1');
			const material1_nodes: ReturnedNodeDataDict = {};
			material1.uiData.setPosition(50, 1150);
			material1.p.material.set('../MAT/meshStandard1');
			material1.params.postCreateSpareParams();
			material1.params.runOnSceneLoadHooks();
			return {node: material1, children: material1_nodes};
		}
		function create_MAT(geo1: GeoObjNode) {
			var MAT = geo1.createNode('materialsNetwork');
			MAT.setName('MAT');
			const MAT_nodes: ReturnedNodeDataDict = {};
			function create_meshStandard1(MAT: MaterialsNetworkSopNode) {
				var meshStandard1 = MAT.createNode('meshStandard');
				meshStandard1.setName('meshStandard1');
				const meshStandard1_nodes: ReturnedNodeDataDict = {};
				meshStandard1.uiData.setPosition(0, 0);
				meshStandard1.p.color.set([0.005605391621829107, 0.008568125615105716, 0.6307571363387763]);
				meshStandard1.params.postCreateSpareParams();
				meshStandard1.params.runOnSceneLoadHooks();
				return {node: meshStandard1, children: meshStandard1_nodes};
			}
			MAT_nodes['meshStandard1'] = create_meshStandard1(MAT);
			if (MAT.childrenController) {
				MAT.childrenController.selection.set([]);
			}
			MAT.uiData.setPosition(-150, 1150);
			MAT.params.postCreateSpareParams();
			MAT.params.runOnSceneLoadHooks();
			return {node: MAT, children: MAT_nodes};
		}
		function create_actor1(geo1: GeoObjNode) {
			var actor1 = geo1.createNode('actor');
			actor1.setName('actor1');
			const actor1_nodes: ReturnedNodeDataDict = {};
			function create_rayFromCursor1(actor1: ActorSopNode) {
				var rayFromCursor1 = actor1.createNode('rayFromCursor');
				rayFromCursor1.setName('rayFromCursor1');
				const rayFromCursor1_nodes: ReturnedNodeDataDict = {};
				rayFromCursor1.uiData.setPosition(550, 350);
				rayFromCursor1.params.postCreateSpareParams();
				rayFromCursor1.params.runOnSceneLoadHooks();
				return {node: rayFromCursor1, children: rayFromCursor1_nodes};
			}
			function create_rayIntersectPlane1(actor1: ActorSopNode) {
				var rayIntersectPlane1 = actor1.createNode('rayIntersectPlane');
				rayIntersectPlane1.setName('rayIntersectPlane1');
				const rayIntersectPlane1_nodes: ReturnedNodeDataDict = {};
				rayIntersectPlane1.uiData.setPosition(750, 450);
				rayIntersectPlane1.params.postCreateSpareParams();
				rayIntersectPlane1.params.runOnSceneLoadHooks();
				return {node: rayIntersectPlane1, children: rayIntersectPlane1_nodes};
			}
			function create_plane1(actor1: ActorSopNode) {
				var plane1 = actor1.createNode('plane');
				plane1.setName('plane1');
				const plane1_nodes: ReturnedNodeDataDict = {};
				plane1.uiData.setPosition(550, 550);
				plane1.params.postCreateSpareParams();
				plane1.params.runOnSceneLoadHooks();
				return {node: plane1, children: plane1_nodes};
			}
			function create_getGeometryNodeObjects1(actor1: ActorSopNode) {
				var getGeometryNodeObjects1 = actor1.createNode('getGeometryNodeObjects');
				getGeometryNodeObjects1.setName('getGeometryNodeObjects1');
				const getGeometryNodeObjects1_nodes: ReturnedNodeDataDict = {};
				getGeometryNodeObjects1.uiData.setPosition(700, -200);
				getGeometryNodeObjects1.params.postCreateSpareParams();
				getGeometryNodeObjects1.params.runOnSceneLoadHooks();
				return {node: getGeometryNodeObjects1, children: getGeometryNodeObjects1_nodes};
			}
			function create_getParam1(actor1: ActorSopNode) {
				var getParam1 = actor1.createNode('getParam');
				getParam1.setName('getParam1');
				const getParam1_nodes: ReturnedNodeDataDict = {};
				getParam1.uiData.setPosition(100, -250);
				getParam1.p.Param.set('../../text1/text');
				getParam1.params.postCreateSpareParams();
				getParam1.params.runOnSceneLoadHooks();
				return {node: getParam1, children: getParam1_nodes};
			}
			function create_setParam1(actor1: ActorSopNode) {
				var setParam1 = actor1.createNode('setParam');
				setParam1.setName('setParam1');
				const setParam1_nodes: ReturnedNodeDataDict = {};
				setParam1.uiData.setPosition(250, -100);
				setParam1.addParam(ParamType.PARAM_PATH, 'Param', '', {
					spare: true,
					editable: false,
					computeOnDirty: false,
					dependentOnFoundParam: false,
				});
				setParam1.addParam(ParamType.FLOAT, 'lerp', 1, {
					spare: true,
					editable: true,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				setParam1.addParam(ParamType.FLOAT, 'val', 0, {
					spare: true,
					editable: false,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				setParam1.params.postCreateSpareParams();
				setParam1.params.runOnSceneLoadHooks();
				return {node: setParam1, children: setParam1_nodes};
			}
			function create_random1(actor1: ActorSopNode) {
				var random1 = actor1.createNode('random');
				random1.setName('random1');
				const random1_nodes: ReturnedNodeDataDict = {};
				random1.uiData.setPosition(-100, 50);
				random1.params.postCreateSpareParams();
				random1.params.runOnSceneLoadHooks();
				return {node: random1, children: random1_nodes};
			}
			function create_mult1(actor1: ActorSopNode) {
				var mult1 = actor1.createNode('mult');
				mult1.setName('mult1');
				const mult1_nodes: ReturnedNodeDataDict = {};
				mult1.uiData.setPosition(0, 50);
				mult1.addParam(ParamType.FLOAT, 'mult0', 1, {
					spare: true,
					editable: false,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				mult1.addParam(ParamType.FLOAT, 'mult1', 1, {
					spare: true,
					editable: true,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				mult1.params.get('mult1')!.set(100);
				mult1.params.postCreateSpareParams();
				mult1.params.runOnSceneLoadHooks();
				return {node: mult1, children: mult1_nodes};
			}
			function create_round1(actor1: ActorSopNode) {
				var round1 = actor1.createNode('round');
				round1.setName('round1');
				const round1_nodes: ReturnedNodeDataDict = {};
				round1.uiData.setPosition(100, 50);
				round1.addParam(ParamType.FLOAT, 'val', 0, {
					spare: true,
					editable: false,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				round1.params.postCreateSpareParams();
				round1.params.runOnSceneLoadHooks();
				return {node: round1, children: round1_nodes};
			}
			function create_setObjectPosition1(actor1: ActorSopNode) {
				var setObjectPosition1 = actor1.createNode('setObjectPosition');
				setObjectPosition1.setName('setObjectPosition1');
				const setObjectPosition1_nodes: ReturnedNodeDataDict = {};
				setObjectPosition1.uiData.setPosition(1150, 50);
				setObjectPosition1.params.postCreateSpareParams();
				setObjectPosition1.params.runOnSceneLoadHooks();
				return {node: setObjectPosition1, children: setObjectPosition1_nodes};
			}
			function create_arrayElement1(actor1: ActorSopNode) {
				var arrayElement1 = actor1.createNode('arrayElement');
				arrayElement1.setName('arrayElement1');
				const arrayElement1_nodes: ReturnedNodeDataDict = {};
				arrayElement1.uiData.setPosition(900, 50);
				arrayElement1.params.postCreateSpareParams();
				arrayElement1.params.runOnSceneLoadHooks();
				return {node: arrayElement1, children: arrayElement1_nodes};
			}
			function create_createObjects1(actor1: ActorSopNode) {
				var createObjects1 = actor1.createNode('createObjects');
				createObjects1.setName('createObjects1');
				const createObjects1_nodes: ReturnedNodeDataDict = {};
				createObjects1.uiData.setPosition(1350, 50);
				createObjects1.params.postCreateSpareParams();
				createObjects1.params.runOnSceneLoadHooks();
				return {node: createObjects1, children: createObjects1_nodes};
			}
			function create_onPointerdown1(actor1: ActorSopNode) {
				var onPointerdown1 = actor1.createNode('onPointerdown');
				onPointerdown1.setName('onPointerdown1');
				const onPointerdown1_nodes: ReturnedNodeDataDict = {};
				onPointerdown1.uiData.setPosition(-200, -150);
				onPointerdown1.params.postCreateSpareParams();
				onPointerdown1.params.runOnSceneLoadHooks();
				return {node: onPointerdown1, children: onPointerdown1_nodes};
			}
			function create_onTick1(actor1: ActorSopNode) {
				var onTick1 = actor1.createNode('onTick');
				onTick1.setName('onTick1');
				const onTick1_nodes: ReturnedNodeDataDict = {};
				onTick1.uiData.setPosition(500, 100);
				onTick1.params.postCreateSpareParams();
				onTick1.params.runOnSceneLoadHooks();
				return {node: onTick1, children: onTick1_nodes};
			}
			function create_multAdd1(actor1: ActorSopNode) {
				var multAdd1 = actor1.createNode('multAdd');
				multAdd1.setName('multAdd1');
				const multAdd1_nodes: ReturnedNodeDataDict = {};
				multAdd1.uiData.setPosition(600, 100);
				multAdd1.addParam(ParamType.FLOAT, 'value', 0, {
					spare: true,
					editable: false,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				multAdd1.addParam(ParamType.FLOAT, 'preAdd', 0, {
					spare: true,
					editable: true,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				multAdd1.addParam(ParamType.FLOAT, 'mult', 1, {
					spare: true,
					editable: true,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				multAdd1.params.get('mult')!.set(0);
				multAdd1.addParam(ParamType.FLOAT, 'postAdd', 0, {
					spare: true,
					editable: true,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				multAdd1.params.postCreateSpareParams();
				multAdd1.params.runOnSceneLoadHooks();
				return {node: multAdd1, children: multAdd1_nodes};
			}
			function create_floatToInt1(actor1: ActorSopNode) {
				var floatToInt1 = actor1.createNode('floatToInt');
				floatToInt1.setName('floatToInt1');
				const floatToInt1_nodes: ReturnedNodeDataDict = {};
				floatToInt1.uiData.setPosition(700, 100);
				floatToInt1.params.postCreateSpareParams();
				floatToInt1.params.runOnSceneLoadHooks();
				return {node: floatToInt1, children: floatToInt1_nodes};
			}
			function create_cookNode2(actor1: ActorSopNode) {
				var cookNode2 = actor1.createNode('cookNode');
				cookNode2.setName('cookNode2');
				const cookNode2_nodes: ReturnedNodeDataDict = {};
				cookNode2.uiData.setPosition(500, -200);
				cookNode2.params.postCreateSpareParams();
				cookNode2.params.runOnSceneLoadHooks();
				return {node: cookNode2, children: cookNode2_nodes};
			}
			function create_getNode1(actor1: ActorSopNode) {
				var getNode1 = actor1.createNode('getNode');
				getNode1.setName('getNode1');
				const getNode1_nodes: ReturnedNodeDataDict = {};
				getNode1.uiData.setPosition(400, -100);
				getNode1.p.Node.set('../../OUT_geometry');
				getNode1.params.postCreateSpareParams();
				getNode1.params.runOnSceneLoadHooks();
				return {node: getNode1, children: getNode1_nodes};
			}
			actor1_nodes['rayFromCursor1'] = create_rayFromCursor1(actor1);
			actor1_nodes['rayIntersectPlane1'] = create_rayIntersectPlane1(actor1);
			actor1_nodes['plane1'] = create_plane1(actor1);
			actor1_nodes['getGeometryNodeObjects1'] = create_getGeometryNodeObjects1(actor1);
			actor1_nodes['getParam1'] = create_getParam1(actor1);
			actor1_nodes['setParam1'] = create_setParam1(actor1);
			actor1_nodes['random1'] = create_random1(actor1);
			actor1_nodes['mult1'] = create_mult1(actor1);
			actor1_nodes['round1'] = create_round1(actor1);
			actor1_nodes['setObjectPosition1'] = create_setObjectPosition1(actor1);
			actor1_nodes['arrayElement1'] = create_arrayElement1(actor1);
			actor1_nodes['createObjects1'] = create_createObjects1(actor1);
			actor1_nodes['onPointerdown1'] = create_onPointerdown1(actor1);
			actor1_nodes['onTick1'] = create_onTick1(actor1);
			actor1_nodes['multAdd1'] = create_multAdd1(actor1);
			actor1_nodes['floatToInt1'] = create_floatToInt1(actor1);
			actor1_nodes['cookNode2'] = create_cookNode2(actor1);
			actor1_nodes['getNode1'] = create_getNode1(actor1);
			actor1_nodes['rayIntersectPlane1'].node.setInput('Ray', actor1_nodes['rayFromCursor1'].node, 'Ray');
			actor1_nodes['rayIntersectPlane1'].node.setInput('Plane', actor1_nodes['plane1'].node, 'Plane');
			actor1_nodes['getGeometryNodeObjects1'].node.setInput('trigger', actor1_nodes['cookNode2'].node, 'trigger');
			actor1_nodes['getGeometryNodeObjects1'].node.setInput('Node', actor1_nodes['getNode1'].node, 'Node');
			actor1_nodes['setParam1'].node.setInput('trigger', actor1_nodes['onPointerdown1'].node, 'trigger');
			actor1_nodes['setParam1'].node.setInput('Param', actor1_nodes['getParam1'].node, 'Param');
			actor1_nodes['setParam1'].node.setInput('val', actor1_nodes['round1'].node, 'round');
			actor1_nodes['mult1'].node.setInput('mult0', actor1_nodes['random1'].node, 'random');
			actor1_nodes['round1'].node.setInput('val', actor1_nodes['mult1'].node, 'product');
			actor1_nodes['setObjectPosition1'].node.setInput(
				'trigger',
				actor1_nodes['getGeometryNodeObjects1'].node,
				'trigger'
			);
			actor1_nodes['setObjectPosition1'].node.setInput('Object3D', actor1_nodes['arrayElement1'].node);
			actor1_nodes['setObjectPosition1'].node.setInput(
				'position',
				actor1_nodes['rayIntersectPlane1'].node,
				'position'
			);
			actor1_nodes['arrayElement1'].node.setInput(0, actor1_nodes['getGeometryNodeObjects1'].node, 'Object3D[]');
			actor1_nodes['arrayElement1'].node.setInput('index', actor1_nodes['floatToInt1'].node, 'int');
			actor1_nodes['createObjects1'].node.setInput('trigger', actor1_nodes['setObjectPosition1'].node, 'trigger');
			actor1_nodes['createObjects1'].node.setInput('child', actor1_nodes['setObjectPosition1'].node, 'Object3D');
			actor1_nodes['multAdd1'].node.setInput('value', actor1_nodes['onTick1'].node, 'time');
			actor1_nodes['floatToInt1'].node.setInput('float', actor1_nodes['multAdd1'].node, 'val');
			actor1_nodes['cookNode2'].node.setInput('trigger', actor1_nodes['setParam1'].node, 'trigger');
			actor1_nodes['cookNode2'].node.setInput('Node', actor1_nodes['getNode1'].node, 'Node');
			if (actor1.childrenController) {
				actor1.childrenController.selection.set([]);
			}
			actor1.uiData.setPosition(-350, 1050);
			actor1.flags.display.set(true);
			actor1.params.postCreateSpareParams();
			actor1.params.runOnSceneLoadHooks();
			return {node: actor1, children: actor1_nodes};
		}
		geo1_nodes['emptyObject1'] = create_emptyObject1(geo1);
		geo1_nodes['transform4'] = create_transform4(geo1);
		geo1_nodes['text1'] = create_text1(geo1);
		geo1_nodes['OUT_geometry'] = create_OUT_geometry(geo1);
		geo1_nodes['material1'] = create_material1(geo1);
		geo1_nodes['MAT'] = create_MAT(geo1);
		geo1_nodes['actor1'] = create_actor1(geo1);
		geo1_nodes['transform4'].node.setInput(0, geo1_nodes['text1'].node);
		geo1_nodes['OUT_geometry'].node.setInput(0, geo1_nodes['material1'].node);
		geo1_nodes['material1'].node.setInput(0, geo1_nodes['transform4'].node);
		geo1_nodes['actor1'].node.setInput(0, geo1_nodes['emptyObject1'].node);
		if (geo1.childrenController) {
			geo1.childrenController.selection.set([geo1_nodes['actor1'].node]);
		}
		geo1.uiData.setPosition(-150, -350);
		geo1.flags.display.set(true);
		geo1.params.postCreateSpareParams();
		geo1.params.runOnSceneLoadHooks();
		return {node: geo1, children: geo1_nodes};
	}
	scene_root_nodes['lights'] = create_lights(root);
	scene_root_nodes['cameras'] = create_cameras(root);
	scene_root_nodes['geo1'] = create_geo1(root);
	scene.play();
	return {
		scene,
		cameraPath: '/cameras/cameras:sopGroup/mapboxCamera1',
		focusedNodes: Object.values(scene_root_nodes['geo1'].children['actor1'].children).map((n) => n.node),
		autoLayout: false,
	};
}
// export const sopMapboxCamera_basic = [
